// Updated UserDashboard.js with MGB medical color scheme and Introduction Quiz support
// src/components/user-dashboard/UserDashboard.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fetchDashboardDataFast, initializeUserSessions, supabase, getUserPracticeLog, savePracticeLog } from "../../supabase";
import MainLayout from "./MainLayout";

// Main UserDashboard Component
export default function UserDashboard() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("All");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [userName, setUserName] = useState("");
  const [practiceData, setPracticeData] = useState({});
  const [practiceDataLoaded, setPracticeDataLoaded] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [logFormData, setLogFormData] = useState({
    skill: '',
    minutes: '',
    practiceType: 'self-guided'
  });
  const [saving, setSaving] = useState(false);
  const [guidedPractices, setGuidedPractices] = useState([]);
  const [isAdminView, setIsAdminView] = useState(false);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [viewingUserEmail, setViewingUserEmail] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, loading: authLoading } = useAuth();

  // Set initial active tab based on mobile state
  useEffect(() => {
    setActiveTab(isMobile ? "Unlocked" : "All");
  }, [isMobile]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if admin is viewing - IMPROVED VERSION
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) return;
      
      const adminView = sessionStorage.getItem('is_admin_view') === 'true';
      const impersonatingId = sessionStorage.getItem('impersonating_user_id');
      const impersonatingEmail = sessionStorage.getItem('impersonating_user_email');
      
      // Fetch user role directly from database
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();
      
      const isUserAdmin = userData?.role === 'admin' || userData?.role === 'researcher';
      
      console.log('🔍 DEBUG - user.id:', user.id);
      console.log('🔍 DEBUG - userData:', userData);
      console.log('🔍 DEBUG - isUserAdmin:', isUserAdmin);
      console.log('🔍 DEBUG - adminView from session:', adminView);
      
      // Show banner if: explicitly in admin view OR user is an admin
      const shouldShowBanner = adminView || isUserAdmin;
      console.log('🔍 DEBUG - shouldShowBanner:', shouldShowBanner);
      
      setIsAdminView(shouldShowBanner);
      setViewingUserId(impersonatingId);
      setViewingUserEmail(impersonatingEmail || (isUserAdmin ? user?.email : ''));
      
      console.log('✅ Admin view state set:', { isAdminView: shouldShowBanner, viewingUserEmail: impersonatingEmail || user?.email });
    };
    
    checkAdminStatus();
  }, [user]);

  // Refetch data when admin view state changes
  useEffect(() => {
    if (isAdminView && viewingUserId && user?.id) {
      console.log('🔄 Admin view activated - fetching data for:', viewingUserEmail);
      fetchUserData();
      fetchPracticeData();
    }
  }, [isAdminView, viewingUserId]);

  // Helper function to calculate time remaining for countdown
  const getTimeUntilUnlock = useCallback((scheduledUnlockTime) => {
    if (!scheduledUnlockTime) return null;
    
    const now = new Date();
    const unlockTime = new Date(scheduledUnlockTime);
    const timeDiff = unlockTime.getTime() - now.getTime();
    
    if (timeDiff <= 0) return null;
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, []);

  // Enhanced fetch function with real backend progress tracking
  const fetchUserData = useCallback(async () => {
    // Use impersonated user ID if in admin view, otherwise use current user
    const effectiveUserId = isAdminView && viewingUserId ? viewingUserId : user?.id;
    
    if (!effectiveUserId || isRefreshing) {
      console.log("❌ Dashboard: No user ID available or already refreshing");
      if (!effectiveUserId) {
        setLoading(false);
      }
      return;
    }

    try {
      console.log("🔄 Dashboard: Starting data fetch for user:", isAdminView ? viewingUserEmail : user?.email);
      console.log("🔍 Using user ID:", effectiveUserId, "Admin view:", isAdminView);
      setIsRefreshing(true);
      setLoading(true);
      setError(null);
      
      // Set user name
      const displayName = user.user_metadata?.username || user.email?.split('@')[0] || "User";
      setUserName(displayName);
      
      // Initialize user sessions first
      await initializeUserSessions(effectiveUserId);
      
      // Fetch sessions with detailed progress data directly from user_sessions table
      const { data: sessionsData, error } = await supabase
        .from('user_sessions')
        .select(
          `
          session_number,
          session_completed,
          session_fully_completed,
          session_started,
          video_watched,
          video_completed,
          quiz_completed,
          pain_level,
          pain_level_timestamp,
          goals_set_timestamp,
          weekly_goals,
          next_session_unlock_time,
          quiz_score,
          video_progress_percentage,
          created_at,
          pre_assessment_completed
        `,
        )
        .eq('user_id', effectiveUserId)
        .order('session_number');

      if (error) {
        console.error('❌ Dashboard: Error fetching sessions:', error);
        throw new Error(error.message);
      }

      if (!sessionsData || sessionsData.length === 0) {
        console.log('📝 Dashboard: No sessions found, using defaults');
        setSessions([]);
        return;
      }

      // Fetch user's session schedule to check unlock times
      const { data: userSchedule } = await supabase
        .from('participant_session_schedule')
        .select('*')
        .eq('user_id', effectiveUserId)
        .single();

      console.log('📅 User session schedule:', userSchedule);
      
      // Session metadata
      const sessionMetadata = [
        { 
          id: "session0", 
          number: 0, 
          title: "Welcome & Program Introduction", 
          subtitle: "Get oriented with the program and set expectations.", 
          estimatedTime: "15 min" 
        },
        { 
          id: "session1", 
          number: 1, 
          title: "Taking Charge of Orofacial Pain", 
          subtitle: "Understand chronic pain and practice deep breathing techniques.", 
          estimatedTime: "20 min" 
        },
        { 
          id: "session2", 
          number: 2, 
          title: "Managing Emotional Challenges in Orofacial Pain", 
          subtitle: "Break negative pain spirals with mindfulness and body scan practice.", 
          estimatedTime: "23 min" 
        },
        { 
          id: "session3", 
          number: 3, 
          title: "Working with Negative Thoughts in Orofacial Pain", 
          subtitle: "Challenge negative thoughts using catch, check, and change techniques.", 
          estimatedTime: "19 min" 
        },
        { 
          id: "session4", 
          number: 4, 
          title: "Not Letting Orofacial Pain Get the Best of Us", 
          subtitle: "Practice acceptance and re-engage in meaningful life activities.", 
          estimatedTime: "22 min" 
        },
        { 
          id: "session5", 
          number: 5, 
          title: "Facing Forward: Rolling with Orofacial Pain & Staying Resilient", 
          subtitle: "Maintain progress and build long-term resilience strategies.", 
          estimatedTime: "22 min" 
        }
      ];
      
      // Fetch schedule
      const { data: schedule } = await supabase
        .from('participant_session_schedule')
        .select('*')
        .eq('user_id', effectiveUserId)
        .single();

      console.log('📅 User session schedule:', schedule);

      // Fetch manual overrides
      const { data: manualOverrides } = await supabase
        .from('manual_session_overrides')
        .select('*')
        .eq('user_id', effectiveUserId);

      console.log('🔓 Manual session overrides:', manualOverrides);

      const transformedSessions = sessionMetadata.map(metadata => {
        const sessionData = sessionsData.find(s => s.session_number === metadata.number);
        
        // Check for manual override for this session
        const override = manualOverrides?.find(o => o.session_number === metadata.number);
        
        // Determine if session is unlocked
        let isUnlocked = metadata.number === 0; // Session 0 is always unlocked
        let scheduledUnlockTime = null;
        
        // PRIORITY 1: Check manual override
        if (override) {
          const overrideStatus = (override.override_status || '').toLowerCase();
          isUnlocked = ['unlocked', 'complete', 'completed'].includes(
            overrideStatus,
          );
          console.log(
            `🔐 Session ${metadata.number} has manual override: ${override.override_status}`,
          );
        } else if (metadata.number === 1) {
          // Session 1 unlocks after Session 0 completion (video + quiz)
          const sessionZero = sessionsData.find((s) => s.session_number === 0);
          if (sessionZero) {
            const isIntroComplete =
              sessionZero.session_completed ||
              (sessionZero.video_completed && sessionZero.quiz_completed);

            isUnlocked = isIntroComplete;

            console.log(`🔍 Dashboard Session 1 unlock check:`, {
              videoCompleted: sessionZero.video_completed,
              quizCompleted: sessionZero.quiz_completed,
              sessionCompleted: sessionZero.session_completed,
              unlocked: isUnlocked,
            });
          }
        } else if (metadata.number > 1) {
          // Sessions 2+ require scheduled unlock time.
          if (schedule) {
            const scheduledUnlockTimeStr =
              schedule[`session_${metadata.number}_unlock_time`];

            if (scheduledUnlockTimeStr) {
              const now = new Date();
              const unlockTime = new Date(scheduledUnlockTimeStr);
              isUnlocked = now >= unlockTime;
              scheduledUnlockTime = unlockTime;

              console.log(
                `📅 Dashboard Session ${metadata.number} scheduled check:`,
                {
                  scheduledTime: unlockTime,
                  currentTime: now,
                  isUnlocked,
                },
              );
            } else {
              isUnlocked = false;
              console.log(
                `🔒 Dashboard Session ${metadata.number} has NO schedule - keeping LOCKED`,
              );
            }
          } else {
            isUnlocked = false;
            console.log(
              `🔒 Dashboard Session ${metadata.number} - NO schedule found for user - keeping LOCKED`,
            );
          }
        }

        // Progress tracking
        const progress = {
          preAssessment: sessionData?.pre_assessment_completed || false,
          goalSetting:
            sessionData?.weekly_goals !== null &&
            sessionData?.weekly_goals !== undefined &&
            sessionData?.weekly_goals !== '',
          video:
            sessionData?.video_watched || sessionData?.video_completed || false,
          quiz: sessionData?.quiz_completed || false,
          //    postAssessment: sessionData?.post_assessment_completed || false
        };

        // Determine completion status with backwards compatibility
        let completed = false;

        if (metadata.number === 0) {
          // Introduction is complete when BOTH video AND quiz are done
          // BUT: Backwards compatibility - if session_completed flag exists, honor it
          completed =
            sessionData?.session_completed ||
            sessionData?.session_fully_completed ||
            (progress.video && progress.quiz);
        } else {
          // Other sessions use existing logic
          completed =
            sessionData?.session_completed ||
            sessionData?.session_fully_completed ||
            false;
        }

        return {
          ...metadata,
          completed: completed,
          unlocked: isUnlocked,
          progress: progress,
          sessionStarted: sessionData?.session_started || false,
          painLevel: sessionData?.pain_level,
          quizScore: sessionData?.quiz_score,
          videoProgress: sessionData?.video_progress_percentage || 0,
          nextSessionUnlockTime: sessionData?.next_session_unlock_time,
          scheduledUnlockTime: scheduledUnlockTime
        };
      });
      
      console.log("✅ Dashboard: Sessions loaded with real progress:", transformedSessions.length);
      setSessions(transformedSessions);
      setRetryCount(0);
      
      // Update guided practices immediately after setting sessions
      console.log("🔍 Updating guided practices after sessions loaded");
      const practices = [
        {
          id: 1,
          name: 'Deep Breathing',
          skill: 'Deep Breathing',
          sessionNumber: 1,
          sessionTitle: 'Taking Charge of Orofacial Pain',
          description:
            "Learn to activate your body's natural relaxation response through controlled breathing techniques.",
          duration: 5,
          color: '#1B365D',
        },
        {
          id: 2,
          name: 'Body Scan',
          skill: 'Body Scan',
          sessionNumber: 2,
          sessionTitle: 'Managing Emotional Challenges in Orofacial Pain',
          description:
            'Develop awareness of physical sensations without judgment to reduce pain-related distress.',
          duration: 6,
          color: '#1B365D',
        },
        {
          id: 3,
          name: 'Catch, Check, and Change',
          skill: 'Catch, Check, and Change',
          sessionNumber: 3,
          sessionTitle: 'Working with Negative Thoughts in Orofacial Pain',
          description:
            'Learn to identify and challenge unhelpful thinking patterns.',
          duration: 2,
          color: '#1B365D',
        },
        {
          id: 4,
          name: 'Goals for Re-engaging in Life',
          skill: 'Goals for Re-engaging in Life',
          sessionNumber: 4,
          sessionTitle: 'Not Letting Orofacial Pain Get the Best of Us',
          description:
            'Learn to incorporate meaningful activities into your daily routine despite pain.',
          duration: 4,
          color: '#1B365D',
        },
        {
          id: 5,
          name: 'Mindfulness of Pain and Discomfort',
          skill: 'Mindfulness of Pain and Discomfort',
          sessionNumber: 5,
          sessionTitle:
            'Facing Forward: Rolling with Orofacial Pain & Staying Resilient',
          description:
            'Develop a new relationship with pain through mindful awareness and acceptance.',
          duration: 3,
          color: '#1B365D',
        },
      ];

      const availablePractices = practices.filter(practice => {
        const session = transformedSessions.find(s => s.number === practice.sessionNumber);
        const hasCompletedVideo = session?.progress?.video || false;
        console.log(
          `🔍 Checking practice ${practice.name} for session ${practice.sessionNumber}: unlocked=${session?.unlocked}, video=${hasCompletedVideo}`,
        );
        return Boolean(session?.unlocked && hasCompletedVideo);
      });
      
      console.log('🎯 Setting guided practices:', availablePractices);
      setGuidedPractices(availablePractices);
      
    } catch (error) {
      console.error("❌ Dashboard: Error fetching data:", error);
      setError(error.message || "Failed to load sessions");
      
      // Auto-retry logic (max 3 attempts)
      if (retryCount < 3) {
        console.log(`🔄 Dashboard: Retrying... (${retryCount + 1}/3)`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000 * (retryCount + 1));
      }
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  }, [user, isAdminView, viewingUserId, viewingUserEmail]);

  // Load data when user is available
  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchUserData();
      fetchPracticeData();
    }
  }, [user?.id, authLoading, isAdminView, viewingUserId]);

  // Separate retry effect to avoid circular dependencies
  useEffect(() => {
    if (retryCount > 0 && retryCount <= 3 && user?.id) {
      const timeoutId = setTimeout(() => {
        fetchUserData();
      }, 2000 * retryCount);
      
      return () => clearTimeout(timeoutId);
    }
  }, [retryCount, user?.id, fetchUserData]);

  // Fetch practice data for calendar
  const fetchPracticeData = useCallback(async () => {
    const effectiveUserId = isAdminView && viewingUserId ? viewingUserId : user?.id;
    if (!effectiveUserId) return;

    try {
      const practiceEntries = await getUserPracticeLog(effectiveUserId);

      const practiceDataMap = {};
      
      practiceEntries.forEach((entry) => {
        // Fix timezone issue - parse date as local date instead of UTC
        const [year, month, day] = entry.practice_date.split('-');
        const practiceDate = new Date(year, month - 1, day);
        const dateKey = practiceDate.toDateString();
        
        // Initialize array if it doesn't exist
        if (!practiceDataMap[dateKey]) {
          practiceDataMap[dateKey] = [];
        }

        // Add practice to the array
        practiceDataMap[dateKey].push({
          skill: entry.skill,
          minutes: entry.minutes,
          practiceType: entry.practice_type,
          sessionNumber: entry.session_number,
          timestamp: entry.timestamp
        });
      });

      setPracticeData(practiceDataMap);
      setPracticeDataLoaded(true);
    } catch (error) {
      console.error("❌ Error fetching practice data:", error);
      setPracticeDataLoaded(true);
    }
  }, [user, isAdminView, viewingUserId]);

  // Get calendar day content for real dates
  const getCalendarDayContent = useCallback((date) => {
    const dateStr = date.toDateString();
    const practicesForDate = practiceData[dateStr];
    
    if (!practicesForDate) return null;

    // Handle both array and single object formats
    let practicesArray;
    if (Array.isArray(practicesForDate)) {
      practicesArray = practicesForDate;
    } else {
      practicesArray = [practicesForDate];
    }

    if (practicesArray.length === 0) return null;

    const totalMinutes = practicesArray.reduce((sum, practice) => sum + practice.minutes, 0);
    const primarySkill = practicesArray[0].skill;
    
    // MGB medical colors
    const colors = {
      0: '#6B7280',
      1: '#1B365D',
      2: '#00A0B0', 
      3: '#4A90A4',
      4: '#7FCDCD',
      5: '#B8E6B8'
    };

    const sessionNumber = practicesArray[0].sessionNumber || 1;

    return {
      hasActivity: true,
      count: practicesArray.length,
      minutes: totalMinutes,
      color: colors[sessionNumber] || colors[1],
      skill: primarySkill
    };
  }, [practiceData]);

  // Generate calendar grid for current month
  const generateCalendarGrid = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // First day of the month
    const firstDay = new Date(currentYear, currentMonth, 1);
    // Last day of the month
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    // Get the day of week for the first day (0 = Sunday)
    const startDayOfWeek = firstDay.getDay();
    // Get total days in month
    const daysInMonth = lastDay.getDate();
    
    // Calculate previous month days to show
    const prevMonth = new Date(currentYear, currentMonth - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    // Calculate how many days to show from next month
    const totalCells = 42; // 6 rows × 7 days
    const daysFromPrevMonth = startDayOfWeek;
    const daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;
    
    const calendarDays = [];
    
    // Previous month days (grayed out)
    for (let i = daysFromPrevMonth; i > 0; i--) {
      const day = daysInPrevMonth - i + 1;
      const date = new Date(currentYear, currentMonth - 1, day);
      calendarDays.push({
        day,
        date,
        isCurrentMonth: false,
        isToday: false,
        isPrevMonth: true
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = date.toDateString() === today.toDateString();
      calendarDays.push({
        day,
        date,
        isCurrentMonth: true,
        isToday,
        isPrevMonth: false
      });
    }
    
    // Next month days (grayed out)
    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      calendarDays.push({
        day,
        date,
        isCurrentMonth: false,
        isToday: false,
        isPrevMonth: false
      });
    }
    
    return calendarDays;
  }, []);

  // Get current month name
  const getCurrentMonthName = useCallback(() => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, []);

  // Handler for when a session becomes unlocked - FIXED: removed circular dependency
  const handleSessionUnlocked = useCallback((sessionId) => {
    console.log(`🔓 Dashboard: Session ${sessionId} unlocked, refreshing...`);
    // Reset retry count and let the retry effect handle the refresh
    setRetryCount(0);
    if (user?.id) {
      fetchUserData();
    }
  }, [user?.id]);

  const handleRetry = () => {
    setRetryCount(1); // Trigger retry
  };

  const handleSessionClick = (session) => {
    if (session.unlocked) {
      const sessionNumber = session.id.replace("session", "");
      const formattedSessionId = sessionNumber.padStart(2, "0");
      navigate(`/session${formattedSessionId}`);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Handle calendar day click for real dates
  const handleCalendarDayClick = useCallback((date, e) => {
    e.stopPropagation();
    
    const today = new Date();
    
    // Don't allow future dates
    if (date > today) return;
    
    setSelectedCalendarDate(date);
    setLogFormData({
      skill: '',
      minutes: '',
      practiceType: 'self-guided'
    });
    setShowInlineForm(true);
  }, []);

  // Handle form submission
  const handleLogSubmit = useCallback(async () => {
    // Block saves in admin view
    if (isAdminView) {
      alert('You are viewing as an admin. Changes cannot be saved.');
      return;
    }
    
    if (!selectedCalendarDate || !logFormData.skill || !logFormData.minutes) {
      return;
    }

    const minutesNum = parseInt(logFormData.minutes);
    if (isNaN(minutesNum) || minutesNum <= 0 || minutesNum > 180) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setSaving(true);

    const practiceLogData = {
      date: selectedCalendarDate.toISOString().split('T')[0],
      skill: logFormData.skill,
      minutes: minutesNum,
      practiceType: logFormData.practiceType,
      sessionNumber: 1
    };

    try {
      await savePracticeLog(user.id, practiceLogData);

      const dateKey = selectedCalendarDate.toDateString();
      const newEntry = {
        skill: logFormData.skill,
        minutes: minutesNum,
        practiceType: logFormData.practiceType,
        sessionNumber: 1,
        timestamp: Date.now()
      };

      // Add to existing practices array for this date
      setPracticeData(prev => ({
        ...prev,
        [dateKey]: [...(prev[dateKey] || []), newEntry]
      }));

      setShowInlineForm(false);
      setSelectedCalendarDate(null);
      setLogFormData({
        skill: '',
        minutes: '',
        practiceType: 'self-guided'
      });

    } catch (error) {
      console.error("Error saving practice log:", error);
    } finally {
      setSaving(false);
    }
  }, [selectedCalendarDate, logFormData, user, isAdminView]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowInlineForm(false);
    setSelectedCalendarDate(null);
    setLogFormData({
      skill: '',
      minutes: '',
      practiceType: 'self-guided'
    });
  }, []);

  // Fixed getAvailableSkills function to match the guided practice library
  const getAvailableSkills = useCallback(() => {
    const skillsBySession = {
      1: ['Deep Breathing'],
      2: ['Body Scan'],
      3: ['Catch, Check, and Change'],
      4: ['Acceptance', 'Goals for Re-engaging in Life'],
      5: ['Mindfulness of Pain and Discomfort'],
    };

    const availableSkills = [];

    // Include skills only when the session is unlocked and its video is completed.
    sessions.forEach((session) => {
      if (session.unlocked && session.progress?.video) {
        const sessionSkills = skillsBySession[session.number] || [];
        availableSkills.push(...sessionSkills);
      }
    });

    // Remove duplicates and return
    return [...new Set(availableSkills)];
  }, [sessions]);

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#F8F9FA'
      }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f1f3f4',
            borderTop: '3px solid #1B365D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ fontFamily: 'Roboto, Arial, sans-serif', color: '#374151' }}>
            Loading authentication...
          </p>
        </div>
      </div>
    );
  }

  // Show error if no user
  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#F8F9FA'
      }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          border: '1px solid #E5E7EB'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Authentication Required</h2>
          <p>Please log in to access your dashboard.</p>
          <button 
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1B365D',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#F8F9FA'
      }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          border: '1px solid #E5E7EB'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Error Loading Dashboard</h2>
          <p style={{ marginBottom: '20px' }}>{error}</p>
          
          {retryCount < 3 && (
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '20px' }}>
              Retrying automatically... ({retryCount}/3)
            </p>
          )}
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={handleRetry}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#6c757d' : '#1B365D',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Retrying...' : 'Try Again'}
            </button>
            <button 
              onClick={() => navigate('/login')}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to determine if session is started (in progress)
  const isSessionStarted = (session) => {
    return (
      session.progress?.preAssessment ||
      session.progress?.goalSetting ||
      session.progress?.video ||
      session.progress?.quiz
    );
  };

  const filteredSessions = activeTab === "All" ? sessions : 
                     activeTab === "Completed" || activeTab === "Complete" ? sessions.filter(session => session.completed) :
                     activeTab === "Unlocked" ? sessions.filter(session => session.unlocked && !session.completed) :
                     sessions.filter(session => session.unlocked && !session.completed);
  const completedSessions = sessions.filter(session => session.completed && session.number !== 0).length;
  const totalSessions = sessions.filter(session => session.number !== 0).length;

  // Dynamic progress message based on completion
  const getProgressMessage = () => {
    const percentage = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;
    
    if (completedSessions === 0) {
      return "Ready to Start!";
    } else if (completedSessions === 1) {
      return "Great Start!";
    } else if (percentage < 50) {
      return "Making Progress!";
    } else if (percentage < 100) {
      return "Almost There!";
    } else {
      return "Amazing Work!";
    }
  };

  // Debug: Log sessions state changes
  console.log('🔍 Component render - Sessions state:', sessions);
  console.log('🔍 Component render - Guided practices state:', guidedPractices);

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#F8F9FA',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      {/* Admin Viewing Banner */}
      {isAdminView && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#FEF3C7',
          borderBottom: '2px solid #F59E0B',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="20" height="20" fill="#F59E0B" viewBox="0 0 24 24">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <div>
              <span style={{ fontWeight: '600', color: '#92400E', fontSize: '14px' }}>
                Viewing Participant Dashboard
              </span>
              <p style={{ fontSize: '12px', color: '#92400E', margin: '4px 0 0 0' }}>
                You are viewing as a researcher.
              </p>
            </div>
          </div>
          
          <button
            onClick={() => {
              navigate('/research');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#1B365D',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0F2744';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1B365D';
            }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Research Dashboard
          </button>
        </div>
      )}
      
      <MainLayout currentSessions={sessions} currentPath={location.pathname}>
        <div style={{
          flex: 1,
          width: '100%',
          padding: isMobile ? '40px 20px 16px 20px' : '52px 40px',
          overflow: 'auto',
          paddingBottom: isMobile ? '80px' : '32px',
          ...(isMobile && {
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          })
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 400px',
            gap: isMobile ? '20px' : '32px',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            ...(isMobile && {
              overflowX: 'hidden',
              boxSizing: 'border-box'
            })
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '32px'
            }}>
              {/* Header Section */}
              <div style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'flex-start',
                marginBottom: '0',
                flexDirection: 'column',
                gap: isMobile ? '0' : '8px',
                width: '100%'
              }}>
                {isMobile ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%'
                  }}>
                    {/* Logo Section */}
                    <div style={{
                      width: 'calc(40% - 8px)',
                      height: '82px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      flexShrink: 0
                    }}>
                      <img 
                        src="/assets/FFWLOGO.png"
                        alt="Face Forward Web Logo"
                        style={{
                          width: '80%',
                          filter: 'brightness(0) saturate(100%) invert(11%) sepia(66%) saturate(1347%) hue-rotate(202deg) brightness(93%) contrast(107%)',
                          height: '60%',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <svg width="40" height="25" fill="#1B365D" viewBox="0 0 24 24" style={{ display: 'none' }}>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>

                    {/* Dashboard Title */}
                    <div style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '16px',
                      padding: '20px 24px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      height: '82px'
                    }}>
                      <h1 style={{
                        fontSize: '32px',
                        fontWeight: '600',
                        color: '#1B365D',
                        margin: 0,
                        letterSpacing: '-1px',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}>
                        Dashboard
                      </h1>
                    </div>
                  </div>
                ) : (
                  <>
                    <h1 style={{
                      fontSize: '48px',
                      fontWeight: '600',
                      color: '#1B365D',
                      margin: 0,
                      letterSpacing: '-1px',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      Dashboard
                    </h1>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '400',
                      color: '#6B7280',
                      margin: 0,
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      Track your progress through the orofacial pain management program
                    </p>
                  </>
                )}
              </div>

              {/* Sessions Section */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: isMobile ? '20px' : '32px',
                minHeight: 'auto',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                marginLeft: 0,
                paddingLeft: isMobile ? '20px' : '32px',
                ...(isMobile && {
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  margin: '0',
                  overflowX: 'hidden'
                })
              }}>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#1B365D',
                  margin: '0 0 24px 0'
                }}>
                  Sessions
                </h3>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '24px',
                  backgroundColor: '#F3F4F6',
                  padding: '4px',
                  borderRadius: '20px',
                  width: 'fit-content'
                }}>
                  {isMobile ? (
                    <>
                      <button
                        onClick={() => setActiveTab("Unlocked")}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          backgroundColor: activeTab === "Unlocked" ? '#1B365D' : 'transparent',
                          color: activeTab === "Unlocked" ? '#FFFFFF' : '#374151',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Unlocked
                      </button>
                      <button
                        onClick={() => setActiveTab("Complete")}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          backgroundColor: activeTab === "Complete" ? '#1B365D' : 'transparent',
                          color: activeTab === "Complete" ? '#FFFFFF' : '#374151',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Complete
                      </button>
                      <button
                        onClick={() => setActiveTab("All")}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          backgroundColor: activeTab === "All" ? '#1B365D' : 'transparent',
                          color: activeTab === "All" ? '#FFFFFF' : '#374151',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        All
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setActiveTab("All")}
                        style={{
                          padding: '8px 24px',
                          borderRadius: '20px',
                          border: 'none',
                          backgroundColor: activeTab === "All" ? '#1B365D' : 'transparent',
                          color: activeTab === "All" ? '#FFFFFF' : '#374151',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setActiveTab("Completed")}
                        style={{
                          padding: '8px 24px',
                          borderRadius: '20px',
                          border: 'none',
                          backgroundColor: activeTab === "Completed" ? '#1B365D' : 'transparent',
                          color: activeTab === "Completed" ? '#FFFFFF' : '#374151',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Completed
                      </button>
                    </>
                  )}
                </div>

                {isMobile && activeTab === "Unlocked" && completedSessions === totalSessions && totalSessions > 0 && (
                  <div style={{
                    backgroundColor: '#ECFDF5',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '16px',
                    border: '1px solid #10B981',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '40px',
                      marginBottom: '12px'
                    }}>
                      🎉
                    </div>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#065F46',
                      margin: '0 0 8px 0'
                    }}>
                      Congratulations!
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: '#047857',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      You have completed all of your sessions
                    </p>
                  </div>
                )}

                {loading && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    padding: '80px 0'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid #f1f3f4',
                      borderTop: '3px solid #1B365D',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ marginLeft: '16px', color: '#374151' }}>
                      Loading sessions...
                    </span>
                  </div>
                )}

                {!loading && filteredSessions.map((session, index) => {
                  const shouldShowCountdown = !session.unlocked && session.scheduledUnlockTime && (() => {
                    const session1 = sessions.find(s => s.number === 1);
                    const hasCompletedSession1 = session1?.completed || false;
                    
                    if (!hasCompletedSession1) {
                      return false;
                    }
                    
                    const lockedSessions = sessions.filter(s => !s.unlocked && s.scheduledUnlockTime);
                    if (lockedSessions.length === 0) return false;
                    
                    const nextToUnlock = lockedSessions.sort((a, b) => a.number - b.number)[0];
                    return session.number === nextToUnlock.number;
                  })();

                  return (
                    <div key={session.id}>
                      <div
                        style={{
                          backgroundColor: session.completed
                            ? '#ECFDF5'
                            : session.unlocked
                              ? isMobile && !isSessionStarted(session)
                                ? '#F8F9FA'
                                : '#EFF6FF'
                              : '#F9FAFB',
                          borderRadius: '12px',
                          padding: isMobile ? '20px' : '20px',
                          marginBottom: '16px',
                          cursor: session.unlocked ? 'pointer' : 'default',
                          opacity: session.unlocked ? 1 : 0.6,
                          transition: 'all 0.2s ease',
                          border: session.completed
                            ? '1px solid #10B981'
                            : session.unlocked
                              ? '1px solid #3B82F6'
                              : '1px solid #E5E7EB',
                          ...(isMobile && {
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                          }),
                        }}
                        onClick={() => handleSessionClick(session)}
                      >
                        {isMobile ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '16px',
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '16px',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: '#1B365D',
                                  color: '#FFFFFF',
                                  borderRadius: '50%',
                                  padding: '12px',
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  minWidth: '48px',
                                  height: '48px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {session.number === 0 ? (
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                  </svg>
                                ) : (
                                  session.number
                                )}
                              </div>

                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    margin: '0 0 4px 0',
                                    lineHeight: '1.3',
                                  }}
                                >
                                  {session.title}
                                </h4>
                                <p
                                  style={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    margin: '0',
                                    lineHeight: '1.4',
                                  }}
                                >
                                  {session.subtitle}
                                </p>
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  marginTop: '4px',
                                }}
                              >
                                {session.completed ? (
                                  <svg
                                    width="24"
                                    height="24"
                                    fill="#10B981"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                  </svg>
                                ) : session.unlocked ? (
                                  isSessionStarted(session) ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="24px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#1B365D"
                                    >
                                      <path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="24px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#00A0B0"
                                    >
                                      <path d="M240-160h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM240-160v-400 400Zm0 80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h280v-80q0-83 58.5-141.5T720-920q83 0 141.5 58.5T920-720h-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80h120q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Z" />
                                    </svg>
                                  )
                                ) : (
                                  <svg
                                    width="24"
                                    height="24"
                                    fill="#9ca3af"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {shouldShowCountdown && (
                              <div
                                style={{
                                  backgroundColor: '#FEF3C7',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  border: '1px solid #F59E0B',
                                  textAlign: 'center',
                                  marginTop: '8px',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '13px',
                                    color: '#92400E',
                                    fontWeight: '600',
                                    marginBottom: '2px',
                                  }}
                                >
                                  Unlocks in:{' '}
                                  {getTimeUntilUnlock(
                                    session.scheduledUnlockTime,
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: '11px',
                                    color: '#B45309',
                                  }}
                                >
                                  {session.scheduledUnlockTime.toLocaleDateString(
                                    'en-US',
                                    {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    },
                                  )}
                                </div>
                              </div>
                            )}

                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                                gap: '8px',
                                width: '100%',
                              }}
                            >
                              {(session.number === 0
                                ? [
                                    {
                                      name: 'Video',
                                      completed: session.progress?.video,
                                    },
                                    {
                                      name: 'Quiz',
                                      completed: session.progress?.quiz,
                                    },
                                  ]
                                : session.number === 1
                                  ? [
                                      {
                                        name: 'Video',
                                        completed: session.progress?.video,
                                      },
                                      {
                                        name: 'Quiz',
                                        completed: session.progress?.quiz,
                                      },
                                    ]
                                  : [
                                      {
                                        name: 'Pre-Assessment',
                                        completed:
                                          session.progress?.preAssessment,
                                      },
                                      {
                                        name: 'Video',
                                        completed: session.progress?.video,
                                      },
                                      {
                                        name: 'Quiz',
                                        completed: session.progress?.quiz,
                                      },
                                    ]
                              ).map((tag, tagIndex) => (
                                <div
                                  key={tagIndex}
                                  style={{
                                    backgroundColor: '#F9FAFB',
                                    color: '#374151',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    border: '1px solid #E5E7EB',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                  }}
                                >
                                  {tag.name}
                                  {tag.completed && (
                                    <svg
                                      width="12"
                                      height="12"
                                      fill="#10B981"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                    </svg>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '16px',
                                width: '100%',
                                maxWidth: '100%',
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: '#1B365D',
                                  color: '#FFFFFF',
                                  borderRadius: '50px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  fontWeight: '400',
                                  minWidth:
                                    session.number === 0 ? '60px' : '90px',
                                  textAlign: 'center',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                }}
                              >
                                {session.number === 0 ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                  </svg>
                                ) : (
                                  `Session ${session.number}`
                                )}
                              </div>

                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    margin: '0 0 4px 0',
                                  }}
                                >
                                  {session.title}
                                </h4>
                                <p
                                  style={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    margin: '0 0 12px 0',
                                  }}
                                >
                                  {session.subtitle}
                                </p>

                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                  }}
                                >
                                  {(session.number === 0
                                    ? [
                                        {
                                          name: 'Video',
                                          completed: session.progress?.video,
                                        },
                                        {
                                          name: 'Quiz',
                                          completed: session.progress?.quiz,
                                        },
                                      ]
                                    : session.number === 1
                                      ? [
                                          {
                                            name: 'Video',
                                            completed: session.progress?.video,
                                          },
                                          {
                                            name: 'Quiz',
                                            completed: session.progress?.quiz,
                                          },
                                        ]
                                      : [
                                          {
                                            name: 'Pre-Assessment',
                                            completed:
                                              session.progress?.preAssessment,
                                          },
                                          {
                                            name: 'Video',
                                            completed: session.progress?.video,
                                          },
                                          {
                                            name: 'Quiz',
                                            completed: session.progress?.quiz,
                                          },
                                        ]
                                  ).map((tag, tagIndex) => (
                                    <div
                                      key={tagIndex}
                                      style={{
                                        backgroundColor: '#F9FAFB',
                                        color: '#374151',
                                        padding: '4px 8px',
                                        borderRadius: '50px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        border: '1px solid #E5E7EB',
                                      }}
                                    >
                                      {tag.name}
                                      {tag.completed && (
                                        <svg
                                          width="12"
                                          height="12"
                                          fill="#10B981"
                                          viewBox="0 0 24 24"
                                        >
                                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                        </svg>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <button
                                style={{
                                  backgroundColor: '#F9FAFB',
                                  color: '#374151',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '20px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: session.unlocked
                                    ? 'pointer'
                                    : 'default',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {session.completed ? (
                                  <>
                                    Complete
                                    <svg
                                      width="12"
                                      height="12"
                                      fill="#10B981"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                    </svg>
                                  </>
                                ) : session.unlocked ? (
                                  isSessionStarted(session) ? (
                                    <>
                                      In Progress
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="12px"
                                        viewBox="0 -960 960 960"
                                        width="12px"
                                        fill="#1B365D"
                                      >
                                        <path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z" />
                                      </svg>
                                    </>
                                  ) : (
                                    <>
                                      Unlocked
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="12px"
                                        viewBox="0 -960 960 960"
                                        width="12px"
                                        fill="#00A0B0"
                                      >
                                        <path d="M240-160h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM240-160v-400 400Zm0 80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h280v-80q0-83 58.5-141.5T720-920q83 0 141.5 58.5T920-720h-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80h120q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Z" />
                                      </svg>
                                    </>
                                  )
                                ) : (
                                  <>
                                    Locked
                                    <svg
                                      width="12"
                                      height="12"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                                    </svg>
                                  </>
                                )}
                              </button>
                            </div>

                            {shouldShowCountdown && (
                              <div
                                style={{
                                  backgroundColor: '#FEF3C7',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  border: '1px solid #F59E0B',
                                  textAlign: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '13px',
                                    color: '#92400E',
                                    fontWeight: '600',
                                    marginBottom: '2px',
                                  }}
                                >
                                  Unlocks in:{' '}
                                  {getTimeUntilUnlock(
                                    session.scheduledUnlockTime,
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: '11px',
                                    color: '#B45309',
                                  }}
                                >
                                  {session.scheduledUnlockTime.toLocaleDateString(
                                    'en-US',
                                    {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    },
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {!loading && sessions.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '80px 0',
                    color: '#374151'
                  }}>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: '500',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      No sessions found
                    </h3>
                    <p style={{ 
                      fontSize: '14px',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      There was an issue loading your sessions. Please try refreshing the page.
                    </p>
                    <button 
                      onClick={handleRetry}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#1B365D',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '20px'
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '16px' : '24px',
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              ...(isMobile && {
                overflowX: 'hidden',
                boxSizing: 'border-box'
              })
            }}>
              {/* Progress Section */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
                padding: isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                width: '100%',
                minHeight: '80px',
                ...(isMobile && {
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                })
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="24" height="24" fill="#1B365D" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '6px'
                  }}>
                    {getProgressMessage()}
                  </div>
                  <div style={{
                    fontSize: '18px',
                    color: '#1B365D',
                    fontWeight: '700',
                    marginBottom: '8px'
                  }}>
                    {completedSessions}/{totalSessions} Sessions Complete
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '20px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0}%`,
                      height: '100%',
                      backgroundColor: '#1B365D',
                      borderRadius: '20px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>

              {/* Calendar and Practice Log Section */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: isMobile ? '16px' : '24px',
                minHeight: showInlineForm ? (isMobile ? '450px' : '500px') : (isMobile ? '320px' : '350px'),
                position: 'relative',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'min-height 0.3s ease',
                ...(isMobile && {
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                })
              }}>
                <div style={{
                  backgroundColor: '#F8F9FA',
                  borderRadius: '12px',
                  padding: isMobile ? '16px' : '24px',
                  marginBottom: '16px',
                  height: isMobile ? '300px' : '280px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #E5E7EB',
                  flexShrink: 0,
                  ...(isMobile && {
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                  })
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    width: isMobile ? '100%' : '280px',
                    maxWidth: isMobile ? '320px' : '280px',
                    margin: '0 auto'
                  }}>
                    <div style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '600',
                      textAlign: 'center',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      {getCurrentMonthName()}
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: isMobile ? '2px' : '4px',
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} style={{
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: '600',
                          textAlign: 'center',
                          color: '#6B7280',
                          marginBottom: '4px',
                          height: isMobile ? '16px' : '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {day}
                        </div>
                      ))}
                      
                      {generateCalendarGrid().map((calendarDay, i) => {
                        const { day, date, isCurrentMonth, isToday, isPrevMonth } = calendarDay;
                        const dayContent = getCalendarDayContent(date);
                        const isFuture = date > new Date();
                        
                        return (
                          <div key={i} style={{
                            width: isMobile ? '28px' : '28px',
                            height: isMobile ? '28px' : '28px',
                            fontSize: isMobile ? '11px' : '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: dayContent?.hasActivity ? '#EFF6FF' : (isToday ? '#1B365D' : 'transparent'),
                            color: isCurrentMonth ? 
                              (dayContent?.hasActivity ? '#1B365D' : (isToday ? '#FFFFFF' : '#374151')) : 
                              '#D1D5DB',
                            border: dayContent?.hasActivity ? '1px solid #3B82F6' : '1px solid transparent',
                            borderRadius: '3px',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            cursor: (isFuture || !isCurrentMonth) ? 'not-allowed' : 'pointer',
                            fontWeight: isCurrentMonth ? '500' : '300',
                            opacity: isCurrentMonth ? 1 : 0.5,
                            ...(isMobile && {
                              minWidth: '28px',
                              maxWidth: '28px',
                              boxSizing: 'border-box'
                            })
                          }}
                          title={dayContent?.hasActivity ? 
                            `${dayContent.skill} - ${dayContent.minutes} minutes` : 
                            (isToday ? 'Today - Click to log practice' : (isFuture ? 'Future date' : 'Click to log practice'))
                          }
                          onClick={(e) => isCurrentMonth && !isFuture && handleCalendarDayClick(date, e)}
                          onMouseEnter={(e) => {
                            if (isCurrentMonth && !isFuture) {
                              e.target.style.transform = 'scale(1.1)';
                              e.target.style.zIndex = '10';
                              if (!dayContent?.hasActivity && !isToday) {
                                e.target.style.backgroundColor = '#F0F9FF';
                              }
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (isCurrentMonth && !isFuture) {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.zIndex = '1';
                              if (!dayContent?.hasActivity && !isToday) {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }
                          }}
                        >
                          {day}
                          {!isMobile && dayContent?.hasActivity && dayContent.count > 1 && (
                            <div style={{
                              position: 'absolute',
                              top: '-4px',
                              right: '-4px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: '#FFFFFF',
                              border: `1px solid #3B82F6`,
                              fontSize: '7px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#1B365D',
                              fontWeight: 'bold'
                            }}>
                              {dayContent.count}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    }}>
                      Home Practice Log
                    </h3>
                    <button style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1B365D',
                      border: '1px solid #3B82F6',
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleNavigation('/practice')}>
                      See Practice Log
                    </button>
                  </div>

                  {showInlineForm && (
                    <div style={{
                      backgroundColor: '#F8F9FA',
                      borderRadius: '12px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <div>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            margin: '0 0 2px 0'
                          }}>
                            Log Practice
                          </h4>
                          <p style={{
                            fontSize: '12px',
                            color: '#6B7280',
                            margin: 0
                          }}>
                            {selectedCalendarDate?.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                        <button
                          onClick={handleCancel}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: '#6B7280',
                            padding: '2px'
                          }}
                        >
                          ×
                        </button>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        marginBottom: '10px'
                      }}>
                        <select
                          value={logFormData.skill}
                          onChange={(e) => setLogFormData(prev => ({ ...prev, skill: e.target.value }))}
                          style={{
                            flex: '2',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            fontSize: '12px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 4px center',
                            backgroundSize: '10px',
                            paddingRight: '20px',
                            minWidth: '0'
                          }}
                        >
                          <option value="">Practice...</option>
                          {getAvailableSkills().map(skill => (
                            <option key={skill} value={skill} style={{ backgroundColor: '#FFFFFF', color: '#374151' }}>
                              {skill}
                            </option>
                          ))}
                        </select>

                        <input
                          type="number"
                          value={logFormData.minutes}
                          onChange={(e) => setLogFormData(prev => ({ ...prev, minutes: e.target.value }))}
                          placeholder="15"
                          min="1"
                          max="180"
                          style={{
                            flex: '0 0 50px',
                            padding: '6px 4px',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            fontSize: '12px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            textAlign: 'center'
                          }}
                        />

                        <select
                          value={logFormData.practiceType}
                          onChange={(e) => setLogFormData(prev => ({ ...prev, practiceType: e.target.value }))}
                          style={{
                            flex: '1',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            fontSize: '12px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 4px center',
                            backgroundSize: '10px',
                            paddingRight: '18px',
                            minWidth: '0'
                          }}
                        >
                          <option value="self-guided" style={{ backgroundColor: '#FFFFFF', color: '#374151' }}>
                            Self-Guided
                          </option>
                          <option value="rewatched-video" style={{ backgroundColor: '#FFFFFF', color: '#374151' }}>
                            Video
                          </option>
                        </select>
                      </div>

                      <button
                        onClick={handleLogSubmit}
                        disabled={saving || !logFormData.skill || !logFormData.minutes}
                        style={{
                          width: '100%',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: (saving || !logFormData.skill || !logFormData.minutes) ? 'not-allowed' : 'pointer',
                          backgroundColor: (saving || !logFormData.skill || !logFormData.minutes) ? '#F3F4F6' : '#1B365D',
                          color: (saving || !logFormData.skill || !logFormData.minutes) ? '#9CA3AF' : '#FFFFFF',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        {saving ? (
                          <>
                            <div style={{
                              width: '10px',
                              height: '10px',
                              border: '2px solid #9ca3af',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }} />
                            Saving...
                          </>
                        ) : (
                          'Log Practice'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Guided Practices Section */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: isMobile ? '16px' : '24px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                ...(isMobile && {
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                })
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: 0
                  }}>
                    Guided Practices
                  </h3>
                  {guidedPractices.length > 0 && (
                    <button style={{
                      backgroundColor: '#F9FAFB',
                      color: '#374151',
                      border: '1px solid #E5E7EB',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleNavigation('/guided-practices')}>
                      View All
                    </button>
                  )}
                </div>
                
                {guidedPractices.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    paddingTop: '4px'
                  }}>
                    {guidedPractices.slice(0, 3).map((practice) => (
                      <div
                        key={practice.id}
                        style={{
                          backgroundColor: '#EFF6FF',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #3B82F6',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onClick={() => handleNavigation('/guided-practices')}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '16px'
                        }}>
                          <div style={{
                            backgroundColor: '#1B365D',
                            color: '#FFFFFF',
                            borderRadius: '50%',
                            padding: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            minWidth: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <img 
                              src={practice.icon || `/assets/${practice.name.toLowerCase().replace(/[,\s]+/g, '-')}.png`}
                              alt={practice.name}
                              style={{
                                width: '28px',
                                height: '28px',
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <img 
                              src={`/assets/${practice.name.toLowerCase().replace(/[,\s&]+/g, '-')}-icon.png`}
                              alt={practice.name}
                              style={{
                                width: '28px',
                                height: '28px',
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)',
                                display: 'none'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}> 
                            <h4 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 4px 0',
                              lineHeight: '1.3'
                            }}>
                              {practice.name}
                            </h4>
                            <p style={{
                              fontSize: '13px',
                              color: '#6B7280',
                              margin: 0,
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {practice.description}
                            </p>
                          </div>

                          <div style={{
                            backgroundColor: '#F9FAFB',
                            color: '#374151',
                            borderRadius: '20px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            border: '1px solid #E5E7EB'
                          }}>
                            S{practice.sessionNumber}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {guidedPractices.length > 3 && (
                      <div style={{
                        textAlign: 'center',
                        padding: '12px'
                      }}>
                        <button style={{
                          backgroundColor: 'transparent',
                          color: '#1B365D',
                          border: 'none',
                          fontSize: '13px',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: '500'
                        }}
                        onClick={() => handleNavigation('/guided-practices')}>
                          +{guidedPractices.length - 3} more practices
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    padding: '32px 20px',
                    textAlign: 'center',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#EFF6FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px'
                    }}>
                      <svg width="20" height="20" fill="#1B365D" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <span style={{
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '4px'
                    }}>
                      Complete sessions to unlock
                    </span>
                    <span style={{
                      color: '#6B7280',
                      fontSize: '12px',
                      lineHeight: '1.4'
                    }}>
                      Guided practice videos become available as you progress through sessions
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            * {
              box-sizing: border-box;
            }
            
            body {
              overflow-x: hidden;
              margin: 0;
              padding: 0;
            }
            
            .dashboard-container, 
            .dashboard-container * {
              max-width: 100vw !important;
              box-sizing: border-box !important;
            }
            
            div, section, article, main {
              max-width: 100% !important;
              overflow-x: hidden !important;
            }
            
            .session-card {
              width: calc(100% - 4px) !important;
              max-width: calc(100% - 4px) !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
          }
        `
      }} />
    </div>
  );
}